import { useIsDark } from '../../contexts/ThemeContext';
import type { CustomNodeData } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

interface ConfigProps {
	nodeId: string;
	data: CustomNodeData;
	onChange: (data: CustomNodeData) => void;
}

// WebhookListenerConfig
export function WebhookListenerConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
				<h3 className="font-medium text-yellow-800">Webhook Listener Node</h3>
				<p className="mt-1 text-sm text-yellow-700">
					This node pauses workflow execution until a webhook is received. Configure the
					expected payload fields below.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Expected Fields <span className="text-red-500">*</span>
					</label>
					<div className="mb-2 text-xs text-gray-500">
						Comma-separated list of required fields in the webhook payload
					</div>
					<input
						type="text"
						value={config.expectedFields || ''}
						onChange={e => handleChange('expectedFields', e.target.value)}
						placeholder="orderId, status, amount"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Secure Token
					</label>
					<div className="mb-2 text-xs text-gray-500">
						Secret token to verify webhook authenticity
					</div>
					<VariablePicker
						nodeId={nodeId}
						value={config.secureToken || ''}
						onChange={value => handleChange('secureToken', value)}
						placeholder="Enter token or {{webhook_secret}}"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Timeout (seconds)
					</label>
					<input
						type="number"
						value={config.timeoutSeconds || 300}
						onChange={e => handleChange('timeoutSeconds', parseInt(e.target.value))}
						min="30"
						max="3600"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
			</div>
		</div>
	);
}

// ApprovalConfig
export function ApprovalConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};
	const isDark = useIsDark();

	// Convert approvers array to string for display
	const approversDisplay = Array.isArray(config.approvers)
		? config.approvers.join(', ')
		: config.approvers || '';

	const handleChange = (field: string, value: any) => {
		// Convert approvers string back to array when saving
		if (field === 'approvers') {
			const approversArray =
				typeof value === 'string'
					? value
							.split(',')
							.map(email => email.trim())
							.filter(email => email.length > 0)
					: value;
			onChange({
				...data,
				config: { ...config, [field]: approversArray },
			});
		} else {
			onChange({
				...data,
				config: { ...config, [field]: value },
			});
		}
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<h3 className="font-medium text-blue-800 dark:text-blue-300">Approval Node</h3>
				<p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
					This node pauses workflow execution until human approval is received from one of
					the configured approvers.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Approvers <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<div className="mb-2 text-xs text-gray-500 dark:text-gray-400">
						Comma-separated list of email addresses
					</div>
					<input
						type="text"
						value={approversDisplay}
						onChange={e => handleChange('approvers', e.target.value)}
						placeholder="admin@company.com, manager@company.com"
						className="px-3 py-2 w-full placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Message <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.message || ''}
						onChange={value => handleChange('message', value)}
						placeholder="Please approve this request for {{customerName}}"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Timeout (hours)
					</label>
					<input
						type="number"
						value={config.timeoutHours || 24}
						onChange={e => handleChange('timeoutHours', parseInt(e.target.value))}
						min="1"
						max="168"
						className="px-3 py-2 w-full placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
					/>
				</div>
			</div>
		</div>
	);
}

// ValidatorConfig
export function ValidatorConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-green-50 rounded-lg border border-green-200">
				<h3 className="font-medium text-green-800">Validator Node</h3>
				<p className="mt-1 text-sm text-green-700">
					This node validates data against configured rules and can branch based on
					validation results.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Field to Validate <span className="text-red-500">*</span>
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.field || ''}
						onChange={value => handleChange('field', value)}
						placeholder="{{email}} or {{phoneNumber}}"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Validation Type
					</label>
					<select
						value={config.validationType || 'required'}
						onChange={e => handleChange('validationType', e.target.value)}
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					>
						<option value="required">Required</option>
						<option value="email">Email Format</option>
						<option value="phone">Phone Number</option>
						<option value="url">URL Format</option>
						<option value="numeric">Numeric</option>
						<option value="regex">Regular Expression</option>
					</select>
				</div>

				{config.validationType === 'regex' && (
					<div>
						<label className="block mb-1 text-sm font-medium text-gray-700">
							Regular Expression
						</label>
						<input
							type="text"
							value={config.regex || ''}
							onChange={e => handleChange('regex', e.target.value)}
							placeholder="^[A-Za-z0-9]+$"
							className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
				)}

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						On Validation Failure
					</label>
					<select
						value={config.onFail || 'stop'}
						onChange={e => handleChange('onFail', e.target.value)}
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					>
						<option value="stop">Stop Workflow</option>
						<option value="continue">Continue Workflow</option>
						<option value="branch">Branch to Different Path</option>
					</select>
				</div>
			</div>
		</div>
	);
}

// StorageConfig
export function StorageConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
				<h3 className="font-medium text-purple-800">Storage Node</h3>
				<p className="mt-1 text-sm text-purple-700">
					This node stores key-value data in external storage with optional expiration.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Storage Key <span className="text-red-500">*</span>
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.key || ''}
						onChange={value => handleChange('key', value)}
						placeholder="user_{{userId}}_profile"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Value to Store <span className="text-red-500">*</span>
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.value || ''}
						onChange={value => handleChange('value', value)}
						placeholder="{{userProfile}} or literal value"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Time to Live (seconds)
					</label>
					<div className="mb-2 text-xs text-gray-500">
						Leave empty for permanent storage
					</div>
					<input
						type="number"
						value={config.ttl || ''}
						onChange={e =>
							handleChange('ttl', e.target.value ? parseInt(e.target.value) : null)
						}
						min="60"
						placeholder="3600"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
			</div>
		</div>
	);
}

// SwitchConfig
export function SwitchConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-red-50 rounded-lg border border-red-200">
				<h3 className="font-medium text-red-800">Switch Node</h3>
				<p className="mt-1 text-sm text-red-700">
					This node routes workflow based on multiple conditions, like a switch statement.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Switch Expression <span className="text-red-500">*</span>
					</label>
					<VariablePicker
						nodeId={nodeId}
						value={config.expression || ''}
						onChange={value => handleChange('expression', value)}
						placeholder="{{userType}} or {{orderAmount}}"
						className="w-full"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Cases (one per line)
					</label>
					<div className="mb-2 text-xs text-gray-500">
						Format: case_value = Branch Label
					</div>
					<textarea
						value={config.cases || ''}
						onChange={e => handleChange('cases', e.target.value)}
						placeholder="premium = Premium Users&#10;trial = Trial Users&#10;basic = Basic Users"
						rows={4}
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Default Case Label
					</label>
					<input
						type="text"
						value={config.defaultCase || ''}
						onChange={e => handleChange('defaultCase', e.target.value)}
						placeholder="Default Path"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
			</div>
		</div>
	);
}

// RetryConfig
export function RetryConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
				<h3 className="font-medium text-orange-800">Retry Node</h3>
				<p className="mt-1 text-sm text-orange-700">
					This node wraps other nodes and retries them on failure with configurable
					strategies.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Maximum Retries <span className="text-red-500">*</span>
					</label>
					<input
						type="number"
						value={config.maxRetries || 3}
						onChange={e => handleChange('maxRetries', parseInt(e.target.value))}
						min="1"
						max="10"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Initial Delay (milliseconds)
					</label>
					<input
						type="number"
						value={config.delayMs || 1000}
						onChange={e => handleChange('delayMs', parseInt(e.target.value))}
						min="100"
						max="60000"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Retry Strategy
					</label>
					<select
						value={config.strategy || 'exponential'}
						onChange={e => handleChange('strategy', e.target.value)}
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					>
						<option value="fixed">Fixed Delay</option>
						<option value="exponential">Exponential Backoff</option>
						<option value="linear">Linear Backoff</option>
					</select>
				</div>

				{config.strategy === 'exponential' && (
					<div>
						<label className="block mb-1 text-sm font-medium text-gray-700">
							Maximum Delay (milliseconds)
						</label>
						<input
							type="number"
							value={config.maxDelayMs || 10000}
							onChange={e => handleChange('maxDelayMs', parseInt(e.target.value))}
							min="1000"
							max="300000"
							className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
				)}
			</div>
		</div>
	);
}

// ParallelConfig
export function ParallelConfig({ nodeId, data, onChange }: ConfigProps) {
	const config = (data.config as any) || {};

	const handleChange = (field: string, value: any) => {
		onChange({
			...data,
			config: { ...config, [field]: value },
		});
	};

	return (
		<div className="p-4 space-y-4">
			<div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
				<h3 className="font-medium text-teal-800">Parallel Node</h3>
				<p className="mt-1 text-sm text-teal-700">
					This node executes multiple workflow branches concurrently and merges results.
				</p>
			</div>

			<div className="space-y-3">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Branch Labels (one per line)
					</label>
					<div className="mb-2 text-xs text-gray-500">
						Each line represents a parallel branch
					</div>
					<textarea
						value={config.branches || ''}
						onChange={e => handleChange('branches', e.target.value)}
						placeholder="Fetch User Profile&#10;Fetch User Preferences&#10;Fetch User Orders"
						rows={4}
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>

				<div>
					<label className="flex gap-2 items-center text-sm font-medium text-gray-700">
						<input
							type="checkbox"
							checked={config.waitForAll !== false}
							onChange={e => handleChange('waitForAll', e.target.checked)}
							className="rounded border-gray-300 focus:ring-blue-500"
						/>
						Wait for All Branches
					</label>
					<div className="mt-1 text-xs text-gray-500">
						If unchecked, continues when any branch completes
					</div>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700">
						Timeout (seconds)
					</label>
					<input
						type="number"
						value={config.timeoutSeconds || 300}
						onChange={e => handleChange('timeoutSeconds', parseInt(e.target.value))}
						min="30"
						max="3600"
						className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
					/>
				</div>
			</div>
		</div>
	);
}
